/**
 * @fileoverview Admin page "Data Repair — Inspection delivery duplicates".
 * @module system-web/pages/DeliveryDuplicateRepairPage
 *
 * Responsibilities:
 * - List departments with their latest scan / apply / revert job state.
 * - Let an admin Scan selected departments, View a scan report, Apply a
 *   repair, or Revert an apply — the latter two behind a typed
 *   department-name confirmation dialog.
 *
 * Dependencies:
 * - react, lucide-react
 * - ../hooks/useDeliveryDuplicateRepair (liveQuery rows + actions)
 *
 * Architecture:
 * - All data comes from the synced `background_jobs` + `tenants` Dexie
 *   tables via the hook — no direct API reads. Actions POST to system-api
 *   through the service; progress streams back through sync.
 * - REMOVABLE with the one-time repair program (stage runbook §8).
 *
 * @component DeliveryDuplicateRepairPage
 * Purpose:     Run scan → review → apply → (revert) for delivery duplicates.
 * Props:       none (route component).
 * Behavior:    Checkbox selection; per-row View/Apply/Revert; typed-confirm
 *              dialog gates Apply and Revert.
 * Data Source: local (Dexie background_jobs/tenants via useDeliveryDuplicateRepair).
 * Constraints: Actions disabled while a job is active for the tenant.
 * Performance: Rows derived by memo over synced jobs.
 */

import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Loader2,
  Play,
  RotateCcw,
  Wrench,
  XCircle,
} from 'lucide-react';
import {
  useDeliveryDuplicateRepair,
  type TenantRepairRow,
} from '../hooks/useDeliveryDuplicateRepair';
import type { DeliveryRepairJob } from '../services/deliveryDuplicateRepairService';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeSince(iso?: string): string {
  if (!iso) return '—';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function isFresh(job?: DeliveryRepairJob): boolean {
  if (!job?.completedAt) return false;
  return Date.now() - Date.parse(job.completedAt) < 24 * 60 * 60 * 1000;
}

function StatusBadge({ status }: { status?: string }) {
  if (!status) return <span className="text-xs text-gray-400">—</span>;
  const map: Record<string, { color: string; label: string }> = {
    completed: { color: 'text-green-700 bg-green-50', label: 'completed' },
    failed: { color: 'text-red-700 bg-red-50', label: 'failed' },
    running: { color: 'text-blue-700 bg-blue-50', label: 'running' },
    pending: { color: 'text-amber-700 bg-amber-50', label: 'pending' },
    cancelled: { color: 'text-gray-600 bg-gray-100', label: 'cancelled' },
  };
  const s = map[status] ?? { color: 'text-gray-600 bg-gray-100', label: status };
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${s.color}`}>{s.label}</span>;
}

// ---------------------------------------------------------------------------
// Confirmation dialog (typed department name)
// ---------------------------------------------------------------------------

interface ConfirmDialogState {
  mode: 'apply' | 'revert';
  tenantName: string;
  jobId: string;
  summary?: DeliveryRepairJob['payload'];
}

function ConfirmDialog({
  state,
  submitting,
  onCancel,
  onConfirm,
}: {
  state: ConfirmDialogState;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState('');
  const matches = typed.trim() === state.tenantName;
  const results = state.summary?.results;
  const summary = state.summary?.summary;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold mb-1">
          {state.mode === 'apply' ? 'Apply duplicate repair' : 'Revert repair'}
        </h3>
        <p className="text-sm text-gray-600 mb-3">
          {state.mode === 'apply'
            ? 'This will soft-delete duplicate delivery records and re-point their references. Every changed document is backed up first.'
            : 'This will restore every document the apply job changed from its backup.'}
        </p>
        {summary && (
          <p className="text-sm text-gray-700 mb-1">
            {summary.reportsWithDuplicates} report(s) with duplicates · {summary.extraRecords} extra record(s)
            {summary.copiesDiffer > 0 ? ` · ${summary.copiesDiffer} set(s) where copies differ` : ''}
          </p>
        )}
        {results && (
          <p className="text-sm text-gray-700 mb-1">
            {results.recordsRemoved} record(s) removed · {results.referencesRepointed} reference(s) re-pointed
            {state.summary?.backupCount != null ? ` · ${state.summary.backupCount} backup doc(s)` : ''}
          </p>
        )}
        <label className="block text-sm font-medium text-gray-700 mt-4 mb-1">
          Type <span className="font-semibold">{state.tenantName}</span> to confirm
        </label>
        <input
          aria-label="Confirm department name"
          className="w-full border rounded-lg px-3 py-2 text-sm"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoFocus
        />
        <div className="flex justify-end gap-2 mt-5">
          <button
            className="px-4 py-2 text-sm rounded-lg border"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white disabled:opacity-50 flex items-center gap-2"
            onClick={onConfirm}
            disabled={!matches || submitting}
          >
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {state.mode === 'apply' ? 'Apply repair' : 'Revert repair'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function DeliveryDuplicateRepairPage() {
  const { rows, isLoading, isSubmitting, error, scanTenants, applyScan, revertApply } = useDeliveryDuplicateRepair();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<ConfirmDialogState | null>(null);

  const selectedIds = useMemo(() => [...selected], [selected]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const canApply = (row: TenantRepairRow): boolean =>
    !!row.latestScan
    && row.latestScan.status === 'completed'
    && isFresh(row.latestScan)
    && !row.activeJob
    && row.latestApply?.payload?.scanJobId !== row.latestScan._id;

  const canRevert = (row: TenantRepairRow): boolean =>
    !!row.latestApply
    && row.latestApply.status === 'completed'
    && !row.activeJob
    && row.latestRevert?.payload?.applyJobId !== row.latestApply._id;

  const onScan = async () => {
    try { await scanTenants(selectedIds); } catch { /* surfaced via error */ }
  };

  const onConfirm = async () => {
    if (!confirm) return;
    try {
      if (confirm.mode === 'apply') await applyScan(confirm.jobId, confirm.tenantName);
      else await revertApply(confirm.jobId, confirm.tenantName);
      setConfirm(null);
    } catch { /* surfaced via error */ }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-1">
        <Wrench className="h-5 w-5 text-gray-500" />
        <h1 className="text-xl font-semibold">Data Repair — Inspection delivery duplicates</h1>
      </div>
      <p className="text-sm text-gray-500 mb-5">
        Scan finds duplicate delivery records per department. Apply soft-deletes the extras and
        re-points their references (with a full backup). Revert restores an apply from its backup.
      </p>

      {error && (
        <div role="alert" className="mb-4 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <XCircle className="h-4 w-4" /> {error}
        </div>
      )}

      <div className="mb-4">
        <button
          className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white disabled:opacity-50 flex items-center gap-2"
          disabled={selectedIds.length === 0 || isSubmitting}
          onClick={onScan}
        >
          {isSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
          Scan selected ({selectedIds.length})
        </button>
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading departments…
        </div>
      ) : (
        <table className="w-full text-sm border rounded-lg overflow-hidden">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="p-3 w-8"></th>
              <th className="p-3">Department</th>
              <th className="p-3">Last scan</th>
              <th className="p-3">Last apply</th>
              <th className="p-3">Active job</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const scan = row.latestScan;
              const apply = row.latestApply;
              const summary = scan?.payload?.summary;
              const isOpen = expanded.has(row.tenant._id);
              return (
                <React.Fragment key={row.tenant._id}>
                  <tr className="border-t">
                    <td className="p-3">
                      <input
                        type="checkbox"
                        aria-label={`Select ${row.tenant.name}`}
                        checked={selected.has(row.tenant._id)}
                        onChange={() => toggle(row.tenant._id)}
                      />
                    </td>
                    <td className="p-3 font-medium">{row.tenant.name}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <StatusBadge status={scan?.status} />
                        <span className="text-xs text-gray-500">{timeSince(scan?.completedAt ?? scan?.startedAt)}</span>
                      </div>
                      {summary && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          {summary.reportsWithDuplicates} dup reports · {summary.extraRecords} extra records
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <StatusBadge status={apply?.status} />
                        <span className="text-xs text-gray-500">{timeSince(apply?.completedAt ?? apply?.startedAt)}</span>
                      </div>
                      {apply?.payload?.results && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          {apply.payload.results.recordsRemoved} removed · {apply.payload.results.referencesRepointed} re-pointed
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      {row.activeJob ? (
                        <div className="flex items-center gap-2 text-xs text-blue-700">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          {row.activeJob.message ?? row.activeJob.status}
                          {row.activeJob.progress?.percent != null && ` ${row.activeJob.progress.percent}%`}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <button
                          className="text-xs px-2 py-1 rounded border flex items-center gap-1 disabled:opacity-40"
                          disabled={!scan || scan.status !== 'completed'}
                          onClick={() => toggleExpand(row.tenant._id)}
                        >
                          {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                          View report
                        </button>
                        <button
                          className="text-xs px-2 py-1 rounded border border-amber-300 text-amber-700 flex items-center gap-1 disabled:opacity-40"
                          disabled={!canApply(row)}
                          onClick={() => setConfirm({
                            mode: 'apply',
                            tenantName: row.tenant.name,
                            jobId: scan!._id,
                            summary: scan?.payload,
                          })}
                        >
                          <ClipboardCheck className="h-3 w-3" /> Apply
                        </button>
                        <button
                          className="text-xs px-2 py-1 rounded border border-red-300 text-red-700 flex items-center gap-1 disabled:opacity-40"
                          disabled={!canRevert(row)}
                          onClick={() => setConfirm({
                            mode: 'revert',
                            tenantName: row.tenant.name,
                            jobId: apply!._id,
                            summary: apply?.payload,
                          })}
                        >
                          <RotateCcw className="h-3 w-3" /> Revert
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isOpen && scan && (
                    <tr className="bg-gray-50">
                      <td></td>
                      <td colSpan={5} className="p-4">
                        <div className="text-xs space-y-1">
                          <p className="font-semibold flex items-center gap-1">
                            <CheckCircle className="h-3.5 w-3.5 text-green-600" /> Scan report — {row.tenant.name}
                          </p>
                          {summary ? (
                            <p>
                              {summary.reports} reports · {summary.records} records ·{' '}
                              {summary.reportsWithDuplicates} with duplicates · {summary.extraRecords} extra ·{' '}
                              {summary.guardLeftovers} guard leftover(s) · {summary.copiesDiffer} copies differ
                            </p>
                          ) : (
                            <p className="text-gray-500">No summary recorded.</p>
                          )}
                          {(scan.payload?.copiesDifferSample ?? []).length > 0 && (
                            <div className="mt-2">
                              <p className="font-medium flex items-center gap-1">
                                <AlertTriangle className="h-3.5 w-3.5 text-amber-600" /> Copies differ — review after apply
                              </p>
                              <ul className="list-disc pl-5 mt-1 space-y-0.5">
                                {scan.payload!.copiesDifferSample!.map((s) => (
                                  <li key={`${s.generatedDocumentId}-${s.survivorId}`}>
                                    {s.reportNumber ?? s.generatedDocumentId}
                                    {s.caseNumber ? ` (${s.caseNumber})` : ''} — differs:{' '}
                                    {s.differingFields.join(', ') || 'user events on extras'}
                                  </li>
                                ))}
                              </ul>
                              {scan.payload?.copiesDifferTruncated && (
                                <p className="text-amber-700 mt-1">List truncated — see scan artifacts for the full set.</p>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="p-6 text-center text-sm text-gray-500">
                  No departments found in the local tenants table.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {confirm && (
        <ConfirmDialog
          state={confirm}
          submitting={isSubmitting}
          onCancel={() => setConfirm(null)}
          onConfirm={onConfirm}
        />
      )}
    </div>
  );
}
